<script>
/**
 * 해외 시세 (금 / 은 / 백금) — HQ 전용.
 *
 * metals-api.com 에서 받아 metal_price_history 에 쌓은 것을 그린다.
 * 기존 현물시세 화면(metals.dev 금/은)과 같은 표를 쓰고 source 로만 갈리므로,
 * 소스를 바꿔 가며 두 공급처가 얼마나 벌어지는지 비교할 수 있다.
 *
 * 백금은 XPT 다. XPD 는 팔라듐이다 - 가격대가 비슷해 바뀌어도 숫자만 봐서는
 * 알아차리기 어렵다. 심볼은 서버 설정에 있다.
 */
import * as echarts from 'echarts'
import { getOverseasPriceChart } from '@/api/overseasPrice.js'

/** 금속별 표시 이름과 선 색. 금/은/백금 순서를 유지한다. */
const METALS = [
  { key: 'GOLD', label: '금', color: '#D4AF37' },
  { key: 'SILVER', label: '은', color: '#9E9E9E' },
  { key: 'PLATINUM', label: '백금', color: '#5C9EAD' },
]

export default {
  name: 'OverseasPricePage',

  data() {
    const toDateStr = (d) => d.toISOString().substring(0, 10)
    const today = new Date()
    const start = new Date(today)
    start.setDate(start.getDate() - 30)

    return {
      METALS,
      search: {
        source: 'METALS_DEV',
        startDate: toDateStr(start),
        endDate: toDateStr(today),
        metals: METALS.map((m) => m.key),
      },
      sourceOptions: [
        // METALS_API 로는 한 건도 들어온 적이 없다(수집기를 켜 본 적이 없다).
        // 고를 수 있게 두면 화면이 빈 채로 열려서 고장으로 보인다.
        { title: 'metals.dev (금/은)', value: 'METALS_DEV' },
      ],
      series: {},
      latest: [],
      sources: [],
      loading: false,
      errorMessage: '',
      noticeMessage: '',
      chartInstance: null,
    }
  },

  computed: {
    /** 선택된 금속 중 실제로 데이터가 있는 것만 */
    drawableMetals() {
      return METALS.filter(
        (m) => this.search.metals.includes(m.key) && (this.series[m.key] || []).length > 0,
      )
    },
    hasData() {
      return this.drawableMetals.length > 0
    },
    /** 소스별 수집 현황에서 현재 선택 소스 한 줄 */
    currentSourceStat() {
      return this.sources.find((s) => s.source === this.search.source) || null
    },
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.fetchChart()
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },

  methods: {
    async fetchChart() {
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await getOverseasPriceChart({
          source: this.search.source,
          metalTypes: this.search.metals,
          startDate: this.search.startDate || undefined,
          endDate: this.search.endDate || undefined,
        })
        if (res.data?.code !== 0) {
          this.errorMessage = res.data?.message || '조회에 실패했습니다.'
          return
        }
        const data = res.data.data || {}
        this.series = data.series || {}
        this.latest = data.latest || []
        this.sources = data.sources || []
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || '조회 중 오류가 발생했습니다.'
      } finally {
        this.loading = false
        // v-if 로 감춰진 컨테이너가 DOM 에 올라온 뒤에 초기화해야 한다.
        await this.$nextTick()
        this.updateChart()
      }
    },

    /** 스케줄러를 기다리지 않고 지금 한 번 받아 본다. 키를 처음 넣었을 때 쓴다. */

    updateChart() {
      if (!this.hasData) {
        if (this.chartInstance) {
          this.chartInstance.dispose()
          this.chartInstance = null
        }
        return
      }
      const el = this.$refs.chartEl
      if (!el) return
      if (!this.chartInstance || this.chartInstance.isDisposed?.()) {
        this.chartInstance = echarts.init(el)
      }

      // x축은 선택된 금속들의 시각을 합집합으로 모은다. 소스가 금속마다
      // 다른 시각을 주더라도 한 축에 올려야 비교가 된다.
      const times = new Set()
      this.drawableMetals.forEach((m) => {
        (this.series[m.key] || []).forEach((p) => times.add(p.quotedAt))
      })
      const xAxis = Array.from(times).sort()

      const series = this.drawableMetals.map((m) => {
        const byTime = new Map((this.series[m.key] || []).map((p) => [p.quotedAt, p.price]))
        return {
          name: m.label,
          type: 'line',
          smooth: true,
          showSymbol: false,
          // 값이 없는 시각은 null 로 둔다. 0 으로 채우면 선이 바닥까지 떨어져
          // 폭락한 것처럼 보인다.
          data: xAxis.map((t) => (byTime.has(t) ? byTime.get(t) : null)),
          connectNulls: true,
          lineStyle: { width: 2, color: m.color },
          itemStyle: { color: m.color },
          // 금과 백금은 만원 단위, 은은 천원 단위라 축을 나눈다.
          yAxisIndex: m.key === 'SILVER' ? 1 : 0,
        }
      })

      this.chartInstance.setOption(
        {
          tooltip: { trigger: 'axis' },
          legend: { data: this.drawableMetals.map((m) => m.label), bottom: 0 },
          grid: { left: 70, right: 70, top: 30, bottom: 50 },
          xAxis: { type: 'category', data: xAxis, boundaryGap: false },
          yAxis: [
            {
              type: 'value',
              name: '금·백금 (원/g)',
              scale: true,
              axisLabel: { formatter: (v) => this.formatPrice(v) },
            },
            {
              type: 'value',
              name: '은 (원/g)',
              scale: true,
              axisLabel: { formatter: (v) => this.formatPrice(v) },
            },
          ],
          series,
        },
        true,
      )
      this.chartInstance.resize()
    },

    handleResize() {
      if (this.chartInstance && !this.chartInstance.isDisposed?.()) {
        this.chartInstance.resize()
      }
    },

    metalLabel(key) {
      return METALS.find((m) => m.key === key)?.label || key
    },

    metalColor(key) {
      return METALS.find((m) => m.key === key)?.color || '#888'
    },

    formatPrice(v) {
      if (v === null || v === undefined) return '-'
      return Number(v).toLocaleString('ko-KR')
    },
  },
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center mb-5">
      <div>
        <div class="text-h5 font-weight-bold">해외 시세 이력</div>
        <div class="text-body-2 text-grey mt-1">
          예전에 모아 둔 금 / 은 기록 — metals.dev
        </div>
      </div>
    </div>

    <!-- 이 화면이 무엇인지 먼저 말한다. 수집을 끈 뒤로 자라지 않는 표라서,
         여기 값만 보고 "시세가 멈췄다" 고 읽으면 안 된다. -->
    <v-alert type="info" variant="tonal" density="comfortable" class="mb-5" border="start">
      <div class="text-body-2">
        <strong>지난 기록을 보는 화면입니다.</strong>
        2026-10-19 에 시세 수집을 공용 서버 한 곳으로 모으면서 이 표는 더 자라지 않습니다.
      </div>
      <div class="text-body-2 mt-1">
        지금 시세와 고객가는 <strong>시세 관리 › 시세 수수료</strong> 에서 봅니다 —
        금은 KRX 국내, 은·백금은 해외 현물 기준입니다.
      </div>
    </v-alert>

    <!-- 검색 -->
    <v-card rounded="lg" elevation="0" border class="mb-5">
      <v-card-text class="pa-4">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-select
              v-model="search.source"
              :items="sourceOptions"
              label="소스"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:260px;"
            />
          </v-col>
          <v-col cols="12" sm="auto" class="mr-3 mb-2 mb-sm-0">
            <v-text-field
              v-model="search.startDate"
              type="date"
              label="시작일"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:160px;"
            />
          </v-col>
          <v-col cols="auto" class="mr-3 mb-2 mb-sm-0 text-grey-darken-1">~</v-col>
          <v-col cols="12" sm="auto" class="mr-4 mb-2 mb-sm-0">
            <v-text-field
              v-model="search.endDate"
              type="date"
              label="종료일"
              density="compact"
              variant="outlined"
              hide-details
              style="min-width:160px;"
            />
          </v-col>
          <v-col cols="12" sm="auto" class="mr-4 mb-2 mb-sm-0">
            <v-btn-toggle v-model="search.metals" multiple density="compact" divided variant="outlined">
              <v-btn v-for="m in METALS" :key="m.key" :value="m.key" size="small">
                {{ m.label }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" variant="flat" :loading="loading" @click="fetchChart">
              <v-icon start>mdi-magnify</v-icon>조회
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      rounded="lg"
      class="mb-4"
      closable
      @click:close="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <v-alert
      v-if="noticeMessage"
      type="success"
      variant="tonal"
      rounded="lg"
      class="mb-4"
      closable
      @click:close="noticeMessage = ''"
    >
      {{ noticeMessage }}
    </v-alert>

    <!-- 최신 시세 카드 -->
    <v-row class="mb-1">
      <v-col v-for="m in METALS" :key="m.key" cols="12" sm="4">
        <v-card rounded="lg" elevation="0" border>
          <v-card-text class="pa-4">
            <div class="d-flex align-center mb-2">
              <span
                :style="{ width: '12px', height: '12px', borderRadius: '50%', background: m.color, display: 'inline-block' }"
                class="mr-2"
              />
              <span class="text-subtitle-2 font-weight-bold">{{ m.label }}</span>
            </div>
            <template v-if="latest.find(l => l.metalType === m.key)">
              <div class="text-h6 font-weight-bold">
                {{ formatPrice(latest.find(l => l.metalType === m.key).price) }}
                <span class="text-caption text-grey ml-1">원/g</span>
              </div>
              <div class="text-caption text-grey mt-1">
                {{ latest.find(l => l.metalType === m.key).quotedAt }} 기준
              </div>
            </template>
            <div v-else class="text-body-2 text-grey">아직 수집된 값이 없습니다.</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 차트 -->
    <v-card rounded="lg" elevation="0" border class="mb-4">
      <v-card-title class="pa-4 pb-2">
        <span class="text-subtitle-2 font-weight-bold">시세 추이</span>
        <v-spacer />
        <span v-if="currentSourceStat" class="text-caption text-grey">
          최근 수집 {{ currentSourceStat.lastQuotedAt || '-' }} ·
          누적 {{ formatPrice(currentSourceStat.rowCount) }}건
        </span>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4">
        <div v-if="loading" class="d-flex justify-center align-center py-16">
          <v-progress-circular indeterminate color="amber-darken-2" size="48" />
        </div>
        <div v-else-if="!hasData" class="text-center text-grey py-16">
          이 기간에 수집된 시세가 없습니다.
          <div class="text-caption mt-2">
            이 기간에는 모아 둔 값이 없습니다. 기간을 넓혀 보세요.
          </div>
        </div>
        <div v-else ref="chartEl" style="width:100%;height:420px;" />
      </v-card-text>
    </v-card>

    <!-- 소스별 수집 현황 -->
    <v-card rounded="lg" elevation="0" border>
      <v-card-title class="pa-4 pb-2">
        <span class="text-subtitle-2 font-weight-bold">소스별 수집 현황</span>
      </v-card-title>
      <v-divider />
      <v-table density="compact">
        <thead>
          <tr>
            <th>소스</th>
            <th class="text-right">금속</th>
            <th class="text-right">누적 건수</th>
            <th>최초</th>
            <th>최근</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in sources" :key="s.source">
            <td class="font-weight-medium">{{ s.source }}</td>
            <td class="text-right">{{ s.metalCount }}</td>
            <td class="text-right">{{ formatPrice(s.rowCount) }}</td>
            <td class="text-caption">{{ s.firstQuotedAt || '-' }}</td>
            <td class="text-caption">{{ s.lastQuotedAt || '-' }}</td>
          </tr>
          <tr v-if="sources.length === 0">
            <td colspan="5" class="text-center text-grey py-6">수집된 시세가 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </div>
</template>
